<template>
  <div class="min-h-screen bg-bochas-bg">
    <!-- Hero con imagen de fondo -->
    <ImageBackgroundSection
      :image-url="joven?.photo_url || ''"
      :alt="joven?.name || 'Joven bochófilo'"
      :title="joven?.name || 'Joven bochófilo'"
      :subtitle="joven?.association || ''"
    />

    <section
      v-if="loading"
      class="px-6 py-20 text-center text-bochas-text-muted"
    >
      Cargando joven...
    </section>

    <!-- Detalle -->
    <RevealOnScroll
      as="article"
      v-else-if="joven"
      class="px-6 lg:px-0 py-12 lg:pt-24 lg:pb-0 max-w-7xl mx-auto"
    >
      <div class="section-header">
        <div class="section-header__icon">
          <IconFbochas name="futuro" :size="32" color="#fff" />
        </div>
        <span class="section-header__label">Futuro</span>
      </div>

      <!-- Info meta -->
      <div
        v-if="joven.association || joven.careerStartDate || joven.careerEndDate || joven.yearsPlaying > 0"
        class="space-y-3 mb-6"
      >
        <div
          v-if="joven.association"
          class="flex items-center gap-2 text-base sm:text-lg"
        >
          <IconFbochas name="location" :size="14" color="white" />
          <span class="text-white/80">{{ joven.association }}</span>
        </div>
        <div
          v-if="joven.careerStartDate || joven.careerEndDate || joven.yearsPlaying > 0"
          class="flex items-center gap-2 text-base sm:text-lg"
        >
          <IconFbochas name="reloj" :size="14" color="white" />
          <span class="text-white/80"
            >{{ careerPeriod(joven, `${joven.yearsPlaying} años jugando`) }}</span
          >
        </div>
      </div>

      <PlayerContactLinks :player="joven" class="mb-6 sm:mb-8" />

      <!-- Imagen principal -->
      <div
        class="detail-motion-media h-[450px] overflow-hidden rounded-xl mb-8 bg-bochas-bg"
      >
        <img
          v-if="joven.photo_url"
          :src="joven.photo_url"
          :alt="joven.name"
          class="w-full h-full object-cover"
          loading="lazy"
        />
        <div
          v-else
          class="flex h-full items-center justify-center bg-degradado-bochas text-6xl font-bold text-white"
        >
          {{ initials }}
        </div>
      </div>

      <!-- Descripción -->
      <RichTextContent
        v-if="joven.bio"
        :html="joven.bio"
        class="text-bochas-text-muted text-sm sm:text-base leading-relaxed mb-10"
      />

      <!-- Logros -->
      <div
        v-if="joven.achievements?.length"
        class="listing-motion-card card-fbochas p-6 sm:p-8 mb-10"
      >
        <div class="flex items-center gap-3 mb-4">
          <IconFbochas name="trophy" :size="26" color="#a52121" />
          <h3 class="text-xl sm:text-2xl font-bold text-white">Logros</h3>
        </div>
        <ul class="space-y-2">
          <li
            v-for="(logro, idx) in joven.achievements"
            :key="idx"
            class="text-white flex items-start gap-2"
          >
            <span class="text-bochas-rojo mt-1">•</span>
            <span>{{ logro }}</span>
          </li>
        </ul>
      </div>

      <!-- Volver -->
      <div class="py-24">
        <VolverButton to="/futuro" />
      </div>
    </RevealOnScroll>

    <section v-else class="px-6 lg:px-0 py-20 max-w-7xl mx-auto text-center">
      <p class="text-bochas-text-muted text-base sm:text-lg">
        Joven bochófilo no encontrado.
      </p>
      <div class="mt-4">
        <VolverButton to="/futuro" text="Volver a Jóvenes Bochófilos" />
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import VolverButton from "@/components/Common/VolverButton.vue";
import ImageBackgroundSection from "@/components/Common/ImageBackgroundSection.vue";
import RevealOnScroll from "@/components/Animations/RevealOnScroll.vue";
import { obtenerJoven } from "@/services/youngPlayers/youngPlayersService";
import { careerPeriod } from "@/utils/playerCareer";
import PlayerContactLinks from "@/components/Common/PlayerContactLinks.vue";
import RichTextContent from "@/components/Common/RichTextContent.vue";
import { applyContentSeo, applyNotFoundSeo } from "@/services/seo/seoService";

const route = useRoute();
const id = computed(() => route.params.slug);
const joven = ref(null);
const loading = ref(true);
const initials = computed(() =>
  String(joven.value?.name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase(),
);

async function load() {
  loading.value = true;
  try {
    joven.value = await obtenerJoven(id.value);
    await applyContentSeo({
      title: joven.value.name,
      description: joven.value.bio,
      image: joven.value.photo_url,
    });
  } catch (_) {
    joven.value = null;
    applyNotFoundSeo();
  } finally {
    loading.value = false;
  }
}

onMounted(load);
watch(id, load);
</script>
